import { Config } from '@backstage/config';
import { ConfigApi } from '@backstage/core-plugin-api';
import {
  MovedState,
  TechRadarApi,
  TechRadarLoaderResponse,
} from '@backstage-community/plugin-tech-radar';

function parseQuadrants(c: Config) {
  return c.getConfigArray('quadrants').map(q => ({
    id: q.getString('id'),
    name: q.getString('name'),
  }));
}

function parseRings(c: Config) {
  return c.getConfigArray('rings').map(r => ({
    id: r.getString('id'),
    name: r.getString('name'),
    color: r.getString('color'),
    description: r.getOptionalString('description'),
  }));
}

/** Ring labels + descriptions for page copy (e.g. SupportButton) — same source as radar rings. */
export function getTechRadarRingHelpLines(
  c: Config | undefined,
): { id: string; name: string; description: string }[] {
  const rings = c?.getOptionalConfigArray('rings');
  if (!rings?.length) {
    return [];
  }
  return rings.flatMap(r => {
    const id = r.getString('id');
    const name = r.getString('name');
    const description = r.getOptionalString('description');
    return description ? [{ id, name, description }] : [];
  });
}

function parseTimeline(entry: Config, ringId: string) {
  const snapshots = entry.getOptionalConfigArray('timeline');
  if (snapshots?.length) {
    return snapshots.map(s => ({
      date: new Date(s.getString('date')),
      ringId: s.getString('ringId'),
      description: s.getOptionalString('description'),
      moved:
        s.getOptionalNumber('moved') === undefined
          ? undefined
          : (s.getNumber('moved') as MovedState),
    }));
  }

  const since =
    entry.getOptionalString('since') ??
    new Date().toISOString().slice(0, 10);

  // Plugin filter matches on title + timeline[0].description only (not entry.description).
  // Use the entry narrative here so History, filter, and blip details stay in sync with YAML.
  const timelineDescription =
    entry.getOptionalString('timelineDescription') ??
    entry.getOptionalString('description') ??
    'Current ring (see responsible team for history).';

  return [
    {
      date: new Date(since),
      ringId,
      description: timelineDescription,
      moved: MovedState.NoChange,
    },
  ];
}

function parseLinks(entry: Config) {
  const links = entry.getOptionalConfigArray('links');
  if (!links?.length) {
    return undefined;
  }
  return links.map(l => ({
    url: l.getString('url'),
    title: l.getString('title'),
  }));
}

function parseEntry(entry: Config): TechRadarLoaderResponse['entries'][number] {
  const id = entry.getString('id');
  const ringId =
    entry.getOptionalString('ring') ??
    entry.getOptionalConfigArray('timeline')?.[0]?.getString('ringId');

  if (!ringId) {
    throw new Error(
      `Tech Radar entry "${id}" must set "ring" or include "timeline" with ringId.`,
    );
  }

  const hasExplicitTimeline = Boolean(
    entry.getOptionalConfigArray('timeline')?.length,
  );
  const narrative = entry.getOptionalString('description');

  return {
    id,
    key: entry.getOptionalString('key') ?? id,
    title: entry.getString('title'),
    quadrant: entry.getString('quadrant'),
    // Single synthesized snapshot: narrative lives on timeline only so the detail dialog
    // does not repeat the same paragraph in Markdown and History.
    description: hasExplicitTimeline ? narrative : undefined,
    url: entry.getOptionalString('url'),
    links: parseLinks(entry),
    timeline: parseTimeline(entry, ringId),
  };
}

function parseEntries(c: Config): TechRadarLoaderResponse['entries'] {
  const list = c.getOptionalConfigArray('entries');
  if (!list?.length) {
    return [];
  }

  return list.map(parseEntry);
}

function parseEntriesFromOptionalConfigArray(
  list: Config[] | undefined,
): TechRadarLoaderResponse['entries'] {
  if (!list?.length) {
    return [];
  }
  return list.map(parseEntry);
}

export class ConfigTechRadarApi implements TechRadarApi {
  constructor(private readonly configApi: ConfigApi) {}

  async load(_id: string | undefined): Promise<TechRadarLoaderResponse> {
    const c = this.configApi.getOptionalConfig('techRadar');
    if (!c) {
      return { quadrants: [], rings: [], entries: [] };
    }

    const preEntries = parseEntriesFromOptionalConfigArray(
      this.configApi.getOptionalConfigArray('techRadarPreEntries'),
    );
    const ctoEntries = parseEntriesFromOptionalConfigArray(
      this.configApi.getOptionalConfigArray('techRadarCtoEntries'),
    );
    const merpaEntries = parseEntriesFromOptionalConfigArray(
      this.configApi.getOptionalConfigArray('techRadarMerpaEntries'),
    );

    return {
      quadrants: parseQuadrants(c),
      rings: parseRings(c),
      entries: [
        ...parseEntries(c),
        ...preEntries,
        ...ctoEntries,
        ...merpaEntries,
      ],
    };
  }
}
